/**
 * In-app browsers (the browser inside Outlook, Facebook, Instagram, LinkedIn, the Google app, LINE, WeChat, or any
 * Android WebView). Meta blocks or breaks Facebook Login there: no Facebook session, popups blocked, and the generic
 * "Sorry, something went wrong" page. The connect screens detect them and suggest Safari or Chrome before sending
 * the user to Meta; the user can still continue.
 */
export type InAppBrowser = 'outlook' | 'facebook' | 'instagram' | 'linkedin' | 'google' | 'line' | 'wechat' | 'webview';

export interface BrowserInfo {
  userAgent?: string;
  /** iOS sets navigator.standalone for a web app opened from the home screen (its user agent has no Safari token) */
  standalone?: boolean;
}

const APPS: readonly [RegExp, InAppBrowser][] = [
  [/Outlook/i, 'outlook'],
  [/FBAN|FBAV|FB_IAB|FBIOS/, 'facebook'],
  [/Instagram/, 'instagram'],
  [/LinkedInApp/, 'linkedin'],
  [/\bGSA\//, 'google'],
  [/\bLine\//, 'line'],
  [/MicroMessenger/, 'wechat'],
];

/** The in-app browser the page runs in, or null for a regular browser (Safari, Chrome, Firefox, Edge, ...). */
export function detectInAppBrowser(info: BrowserInfo | undefined): InAppBrowser | null {
  const ua = info?.userAgent ?? '';
  if (!ua) return null;
  for (const [re, app] of APPS) if (re.test(ua)) return app;
  if (/Android/.test(ua) && /;\s*wv\)/.test(ua)) return 'webview';
  // iOS browsers (Safari, Chrome, Firefox, Edge) all carry "Safari/"; an embedded WKWebView does not.
  if (/iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua) && !info?.standalone) return 'webview';
  return null;
}

/** Reads the current browser once; null on the server or in tests without a navigator. */
export function currentInAppBrowser(): InAppBrowser | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as Navigator & { standalone?: boolean };
  return detectInAppBrowser({ userAgent: nav.userAgent, standalone: nav.standalone === true });
}

/**
 * One gate per connect screen: in an in-app browser the first connect is held so the notice can be shown; after
 * "Continue anyway" it never holds again on that screen.
 */
export function connectGate(inApp: InAppBrowser | null) {
  let acknowledged = false;
  return {
    inApp,
    shouldHold: () => inApp !== null && !acknowledged,
    acknowledge: () => { acknowledged = true; },
  };
}
export type ConnectGate = ReturnType<typeof connectGate>;
